/**
 * CDN Token Management Utility
 * Gestisce l'autenticazione con le API CDN (Emma Suite)
 * Il token viene cachato in memoria poiché non scade
 */

let cachedToken: string | null = null;
let tokenPromise: Promise<string> | null = null;

interface CDNTokenResponse {
  success: boolean;
  token: string;
  expires_at: string | null;
}

/**
 * Determina il channel_id corretto basato sull'aeroporto
 * @returns string - Channel ID per l'aeroporto corrente
 */
function getChannelId(): string {
  const airport = process.env.NEXT_PUBLIC_AIRPORT_REFERENCE || 'MXP';

  if (airport === 'LIN') {
    return process.env.NEXT_PUBLIC_CDN_CHANNEL_ID_LIN || '1770669';
  }

  return process.env.NEXT_PUBLIC_CDN_CHANNEL_ID_MXP || '1770668';
}

/**
 * Ottiene il token Bearer per le API CDN
 * Il token viene cachato in memoria e riutilizzato per tutte le chiamate successive
 * @returns Promise<string> - Token Bearer (senza "Bearer " prefix)
 */
export async function getCDNToken(): Promise<string> {
  // Se abbiamo già un token cached, lo restituiamo
  if (cachedToken) {
    return cachedToken;
  }

  // Se c'è già una richiesta in corso, attendiamo quella
  if (tokenPromise) {
    return tokenPromise;
  }

  // Creiamo una nuova richiesta
  tokenPromise = (async () => {
    try {
      // Chiamiamo l'API route invece del CDN direttamente
      const response = await fetch('/api/cdn/token', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`[CDN] Token request failed: ${response.status} ${response.statusText}`);
      }

      const data: CDNTokenResponse = await response.json();

      if (!data.success || !data.token) {
        throw new Error('[CDN] Invalid token response');
      }

      // Rimuoviamo "Bearer " se presente nella risposta
      const token = data.token.replace(/^Bearer\s+/i, '');

      // Cachiamo il token
      cachedToken = token;

      return token;
    } catch (error) {
      console.error('[CDN] Token fetch error:', error);
      throw error;
    } finally {
      // Reset della promise per permettere retry in caso di errore
      tokenPromise = null;
    }
  })();

  return tokenPromise;
}

/**
 * Invalida il token cached (utile per forzare un refresh)
 */
export function invalidateCDNToken(): void {
  cachedToken = null;
  tokenPromise = null;
}

/**
 * Effettua una chiamata autenticata alle API CDN tramite API routes Next.js
 * @param endpoint - Endpoint relativo (es: 'api/sea/passthrough-categories')
 * @param options - Opzioni fetch aggiuntive
 * @returns Promise<T> - Response data
 */
export async function fetchCDN<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    const token = await getCDNToken();

    // Determiniamo quale API route usare basandoci sull'endpoint
    let apiRoute = '/api/cdn/';

    // Parsing dell'endpoint per distinguere tra singolo e multiplo
    if (endpoint.includes('passthrough-product/')) {
      // Singolo prodotto: /api/sea/passthrough-product/123
      const productId = endpoint.split('passthrough-product/')[1].split('?')[0];
      apiRoute += `product/${productId}`;
    } else if (endpoint.includes('passthrough-products')) {
      // Lista prodotti
      apiRoute += 'products';
    } else if (endpoint.includes('passthrough-category/')) {
      // Singola categoria: /api/sea/passthrough-category/456
      const categoryId = endpoint.split('passthrough-category/')[1].split('?')[0];
      apiRoute += `category/${categoryId}`;
    } else if (endpoint.includes('passthrough-categories')) {
      // Lista categorie
      apiRoute += 'categories';
    } else {
      throw new Error(`[CDN] Unknown endpoint: ${endpoint}`);
    }

    // Estraiamo i parametri query dall'endpoint se presenti
    const url = new URL(endpoint, 'http://dummy');
    const params = new URLSearchParams(url.search);
    params.set('token', token);

    // Aggiungiamo channel_id se non presente
    if (!params.has('channel_id')) {
      params.set('channel_id', getChannelId());
    }

    const fullUrl = `${apiRoute}?${params}`;

    const response = await fetch(fullUrl, {
      ...options,
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      // Se otteniamo 401, invalidiamo il token e ritentiamo una volta
      if (response.status === 401 && cachedToken) {
        console.warn('[CDN] 401 Unauthorized, invalidating token and retrying...');
        invalidateCDNToken();

        // Retry con nuovo token
        const newToken = await getCDNToken();
        params.set('token', newToken);

        const retryResponse = await fetch(`${apiRoute}?${params}`, {
          ...options,
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            ...options.headers,
          },
        });

        if (!retryResponse.ok) {
          throw new Error(`[CDN] Request failed after retry: ${retryResponse.status}`);
        }

        return retryResponse.json();
      }

      throw new Error(`[CDN] Request failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  } catch (error) {
    console.error('[CDN] Fetch error:', error);
    throw error;
  }
}

/**
 * Interfacce TypeScript per le risposte CDN
 * L'output è uguale a quello delle API BigCommerce esistenti
 */
export interface ICDNProduct {
  id: number;
  name: string;
  type?: string;
  sku?: string;
  description: string;
  price: number;
  sale_price: number;
  tax_class_id: number;
  categories: number[];
  brand_id?: number;
  brand_name?: string | null;
  inventory_level?: number;
  is_visible: boolean;
  custom_url?: {
    url: string;
    is_customized: boolean;
  };
  image?: {
    id: number;
    product_id: number;
    is_thumbnail: boolean;
    url_thumbnail: string;
    url_standard: string;
  };
  primary_image?: {
    url_thumbnail?: string;
    url_standard?: string;
  };
  images?: Array<{
    url_thumbnail?: string;
    url_standard?: string;
  }>;
  variants?: ICDNProductVariant[];
  channels?: number[];
  custom_fields?: any;
}

export interface ICDNProductVariant {
  id: number;
  product_id: number;
  sku: string;
  price: number;
  sale_price: number;
  inventory_level: number;
}

export interface ICDNCategory {
  id: number;
  parent_id: number;
  name: string;
  description: string;
  is_visible: boolean;
  custom_url: {
    url: string;
    is_customized: boolean;
  };
}

/**
 * Recupera tutti i prodotti dalle API CDN
 * @param channelId - ID del canale (opzionale)
 * @param locale - Codice lingua (es: 'it', 'en') per filtrare i prodotti
 * @param categories - ID categoria per filtrare i prodotti (es: '51')
 * @returns Promise<ICDNProduct[]>
 */
export async function getCDNProducts(channelId?: string, locale?: string, categories?: string): Promise<ICDNProduct[]> {
  try {
    // Costruzione dinamica dei query parameters
    const params = new URLSearchParams();

    if (channelId) {
      params.set('channel_id', channelId);
    }

    if (locale) {
      params.set('locale', locale);
    }

    if (categories) {
      params.set('categories', categories);
    }

    const queryString = params.toString();
    const endpoint = queryString
      ? `api/sea/passthrough-products?${queryString}`
      : 'api/sea/passthrough-products';

    const products = await fetchCDN<ICDNProduct[]>(endpoint);
    console.log(`[CDN] Fetched ${products.length} products` +
      (channelId ? ` for channel ${channelId}` : '') +
      (locale ? ` in locale ${locale}` : '') +
      (categories ? ` in category ${categories}` : ''));
    return products;
  } catch (error) {
    console.error('[CDN] getCDNProducts error:', error);
    return [];
  }
}

/**
 * Recupera un prodotto specifico dalle API CDN
 * @param productId - ID del prodotto
 * @param channelId - ID del canale (opzionale)
 * @returns Promise<ICDNProduct | null>
 */
export async function getCDNProduct(productId: string | number, channelId?: string): Promise<ICDNProduct | null> {
  try {
    const endpoint = channelId
      ? `api/sea/passthrough-product/${productId}?channel_id=${channelId}`
      : `api/sea/passthrough-product/${productId}`;

    const product = await fetchCDN<ICDNProduct>(endpoint);
    return product;
  } catch (error) {
    console.error(`[CDN] getCDNProduct error for product ${productId}:`, error);
    return null;
  }
}

/**
 * Recupera più prodotti specifici dalle API CDN
 * @param productIds - Array di ID prodotti (DEVONO essere numerici, non UUID)
 * @param channelId - ID del canale (opzionale)
 * @returns Promise<ICDNProduct[]>
 */
export async function getCDNProductsByIds(productIds: (string | number)[], channelId?: string): Promise<ICDNProduct[]> {
  try {
    // Filtra solo ID numerici e converte in numeri
    const numericIds = productIds
      .map(id => typeof id === 'string' ? parseInt(id, 10) : id)
      .filter(id => !isNaN(id));

    if (numericIds.length === 0) {
      console.warn('[CDN] No valid numeric product IDs provided');
      return [];
    }

    const promises = numericIds.map(id => getCDNProduct(id, channelId));
    const results = await Promise.all(promises);

    // Filtra null e oggetti di errore
    const validProducts = results.filter((product): product is ICDNProduct =>
      product !== null && typeof product === 'object' && 'id' in product && !('error' in product)
    );

    return validProducts;
  } catch (error) {
    console.error('[CDN] getCDNProductsByIds error:', error);
    return [];
  }
}

/**
 * Recupera tutte le categorie dalle API CDN
 * @param channelId - ID del canale (opzionale)
 * @returns Promise<ICDNCategory[]>
 */
export async function getCDNCategories(channelId?: string, categories?: string): Promise<ICDNCategory[]> {
  try {
    const params = new URLSearchParams();
    if (channelId) params.set('channel_id', channelId);
    if (categories) params.set('categories', categories);
    const queryString = params.toString();
    const endpoint = queryString
      ? `api/sea/passthrough-categories?${queryString}`
      : 'api/sea/passthrough-categories';

    const result = await fetchCDN<ICDNCategory[]>(endpoint);
    console.log(`[CDN] Fetched ${result.length} categories${channelId ? ` for channel ${channelId}` : ''}`);
    return result;
  } catch (error) {
    console.error('[CDN] getCDNCategories error:', error);
    return [];
  }
}

/**
 * Recupera una categoria specifica dalle API CDN
 * @param categoryId - ID della categoria
 * @param channelId - ID del canale (opzionale)
 * @returns Promise<ICDNCategory | null>
 */
export async function getCDNCategory(categoryId: string | number, channelId?: string): Promise<ICDNCategory | null> {
  try {
    const endpoint = channelId
      ? `api/sea/passthrough-category/${categoryId}?channel_id=${channelId}`
      : `api/sea/passthrough-category/${categoryId}`;

    const category = await fetchCDN<ICDNCategory>(endpoint);
    return category;
  } catch (error) {
    console.error(`[CDN] getCDNCategory error for category ${categoryId}:`, error);
    return null;
  }
}
